import type { ICampaign, ICampaignParticipantDetail, IInfluencer } from '../redux/buzzbridge/types';

export function normalizeUnreadCount(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.floor(value);
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        if (Number.isFinite(n) && n > 0) return Math.floor(n);
    }
    return 0;
}

export function formatUnreadBadgeCount(count: number): string | null {
    if (count <= 0) return null;
    return count > 99 ? '99+' : String(count);
}

export function sumParticipantUnreadCounts(participants: Array<{ unread_message_count?: number }> | undefined): number {
    return (participants ?? []).reduce((sum, row) => sum + normalizeUnreadCount(row.unread_message_count), 0);
}

function clearParticipantUnreadInList<T extends { _id?: string; unread_message_count?: number }>(
    items: T[] | undefined,
    participantId: string,
): T[] {
    if (!items?.length) return items ?? [];
    return items.map(item => (String(item._id) === participantId ? { ...item, unread_message_count: 0 } : item));
}

function setParticipantUnreadInList<T extends { _id?: string; unread_message_count?: number }>(
    items: T[] | undefined,
    participantId: string,
    count: number,
): T[] {
    if (!items?.length) return items ?? [];
    const normalized = normalizeUnreadCount(count);
    return items.map(item =>
        String(item._id) === participantId ? { ...item, unread_message_count: normalized } : item,
    );
}

function clearAllParticipantUnreadInList<T extends { unread_message_count?: number }>(items: T[] | undefined): T[] {
    if (!items?.length) return items ?? [];
    return items.map(item => ({ ...item, unread_message_count: 0 }));
}

export function applyThreadReadToCampaign(
    campaign: ICampaign | null | undefined,
    participantId: string,
): ICampaign | null | undefined {
    if (!campaign) return campaign;
    const participants = clearParticipantUnreadInList(campaign.participants, participantId);
    const pending_requests = clearParticipantUnreadInList(campaign.pending_requests, participantId);
    const pending_invites = clearParticipantUnreadInList(campaign.pending_invites, participantId);
    return {
        ...campaign,
        participants,
        pending_requests,
        pending_invites,
        unread_messages_total: sumParticipantUnreadCounts([...participants, ...pending_requests, ...pending_invites]),
    };
}

export function applyAllThreadsReadToCampaign(campaign: ICampaign | null | undefined): ICampaign | null | undefined {
    if (!campaign) return campaign;
    const participants = clearAllParticipantUnreadInList(campaign.participants);
    const pending_requests = clearAllParticipantUnreadInList(campaign.pending_requests);
    const pending_invites = clearAllParticipantUnreadInList(campaign.pending_invites);
    return {
        ...campaign,
        participants,
        pending_requests,
        pending_invites,
        unread_messages_total: 0,
    };
}

export function applyThreadReadToInfluencerProfile(
    influencer: IInfluencer | null | undefined,
    participantId: string,
): IInfluencer | null | undefined {
    if (!influencer) return influencer;

    const active_jobs = clearParticipantUnreadInList(influencer.active_jobs, participantId);
    const requests = clearParticipantUnreadInList(influencer.requests, participantId);
    const invitations = clearParticipantUnreadInList(influencer.invitations, participantId);
    const completed_jobs = clearParticipantUnreadInList(influencer.completed_jobs, participantId);

    return {
        ...influencer,
        active_jobs,
        requests,
        invitations,
        completed_jobs,
        unread_messages_total: sumParticipantUnreadCounts([
            ...active_jobs,
            ...requests,
            ...invitations,
            ...completed_jobs,
        ]),
    };
}

export function setParticipantUnreadOnCampaign(
    campaign: ICampaign | null | undefined,
    participantId: string,
    unreadCount: number,
): ICampaign | null | undefined {
    if (!campaign) return campaign;
    const participants = setParticipantUnreadInList(campaign.participants, participantId, unreadCount);
    const pending_requests = setParticipantUnreadInList(campaign.pending_requests, participantId, unreadCount);
    const pending_invites = setParticipantUnreadInList(campaign.pending_invites, participantId, unreadCount);
    return {
        ...campaign,
        participants,
        pending_requests,
        pending_invites,
        unread_messages_total: sumParticipantUnreadCounts([...participants, ...pending_requests, ...pending_invites]),
    };
}

export function setParticipantUnreadOnInfluencerProfile(
    influencer: IInfluencer | null | undefined,
    participantId: string,
    unreadCount: number,
): IInfluencer | null | undefined {
    if (!influencer) return influencer;
    const active_jobs = setParticipantUnreadInList(influencer.active_jobs, participantId, unreadCount);
    const requests = setParticipantUnreadInList(influencer.requests, participantId, unreadCount);
    const invitations = setParticipantUnreadInList(influencer.invitations, participantId, unreadCount);
    const completed_jobs = setParticipantUnreadInList(influencer.completed_jobs, participantId, unreadCount);
    return {
        ...influencer,
        active_jobs,
        requests,
        invitations,
        completed_jobs,
        unread_messages_total: sumParticipantUnreadCounts([
            ...active_jobs,
            ...requests,
            ...invitations,
            ...completed_jobs,
        ]),
    };
}

export function participantUnreadCount(participant: ICampaignParticipantDetail | null | undefined): number {
    return normalizeUnreadCount(participant?.unread_message_count);
}
